import type {
  CreateOnboardingTemplateFromDescriptionRequest,
  ReplaceOnboardingTemplateDraftFromDescriptionRequest,
} from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ONBOARDING_DRAFT_EXAMPLES } from '@/products/onboarding/features/onboarding-template-draft-examples';

const REPLACE_WARNING =
  'Der aktuelle Entwurf wird ersetzt. Ungespeicherte Änderungen gehen verloren. Die veröffentlichte Fassung bleibt.';

type Kind = CreateOnboardingTemplateFromDescriptionRequest['kind'];

export function OnboardingTemplateDraftDialog(props: {
  mode: 'create' | 'replace';
  open: boolean;
  pending: boolean;
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onCreate: (body: CreateOnboardingTemplateFromDescriptionRequest) => void;
  onReplace: (body: ReplaceOnboardingTemplateDraftFromDescriptionRequest) => void;
}) {
  const [kind, setKind] = useState<Kind>('onboarding');
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    if (props.mode === 'create') {
      props.onCreate({ kind, name: name.trim(), prompt: prompt.trim() });
      return;
    }
    props.onReplace({ prompt: prompt.trim() });
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {props.mode === 'create' ? 'Mit KI entwerfen' : 'Entwurf aus Beschreibung'}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          {props.mode === 'replace' ? <p className="text-sm">{REPLACE_WARNING}</p> : null}
          {props.mode === 'create' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="onboarding-ai-kind">Art</Label>
                <Select value={kind} onValueChange={(value) => setKind(value as Kind)}>
                  <SelectTrigger id="onboarding-ai-kind" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onboarding">Eintritt</SelectItem>
                    <SelectItem value="offboarding">Austritt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="onboarding-ai-name">Name</Label>
                <Input
                  id="onboarding-ai-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
            </>
          ) : null}
          {props.mode === 'create' ? (
            <div className="space-y-2">
              <p id="onboarding-ai-examples" className="text-sm font-medium">
                Beispiele
              </p>
              <div
                role="group"
                aria-labelledby="onboarding-ai-examples"
                className="flex flex-wrap gap-2"
              >
                {ONBOARDING_DRAFT_EXAMPLES[kind].map((example) => (
                  <Button
                    key={example.style}
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setName(example.name);
                      setPrompt(example.prompt);
                    }}
                  >
                    {example.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="onboarding-ai-prompt">Beschreibung</Label>
            <Textarea
              id="onboarding-ai-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
            />
          </div>
          {props.error ? (
            <p className="text-sm text-destructive" role="alert">
              {props.error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={props.pending}>
              {props.mode === 'create' ? 'Entwurf erstellen' : 'Ersetzen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
